import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { api } from '../config/api';
import logo from '../assets/SFTLogo.jpeg';
import { 
  masterPanel, 
  masterFormContainer,
  masterFormGrid3,
  masterFormGridRow,
  masterInputLabel,
  masterSelect, 
  masterAccentButton, 
  masterPrimaryButton,
  masterInput,
  masterSectionTitle,
  masterFormActions,
  masterStickyHeadRow,
  masterTableHeaderCell,
  masterTableHeaderCellCenter,
  masterTable,
  masterTableWrap,
  masterTableRow,
  masterTableData,
  masterTableActionCell,
  masterEditButton,
  masterDeleteButton
} from './MasterCssClass';

const formatBillingPeriod = (periodStr) => {
  if (!periodStr || !periodStr.includes('-')) return periodStr;
  const [year, month] = periodStr.split('-');
  const date = new Date(parseInt(year, 10), parseInt(month, 10) - 1, 1);
  const monthName = date.toLocaleString('en-US', { month: 'short' }).toUpperCase();
  return `${monthName}'${year}`;
};
const getFormattedDate = (date = new Date()) => {
  const d = new Date(date);
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = d.getFullYear();
  return `${day}-${month}-${year}`;
};

const InvoiceGenerator = () => {
  const [clients, setClients] = useState([]);
  const [bankingList, setBankingList] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [showList, setShowList] = useState(false);
  const [editId, setEditId] = useState(null);
  
  const [selectedClient, setSelectedClient] = useState('');
  const [selectedBanking, setSelectedBanking] = useState('');
  const [selectedSession, setSelectedSession] = useState('');
  const [printingInvoice, setPrintingInvoice] = useState(null);

  // Multiple Billing Periods state
  const [billingPeriods, setBillingPeriods] = useState(['']);

  // Multiple Line Items state (blank defaults, no 0)
  const [items, setItems] = useState([
    { description: '', hsnSac: '998314', quantity: '', rate: '' }
  ]);

  const [invoiceData, setInvoiceData] = useState({
    invoiceNumber: '',
    invoiceDate: getFormattedDate(),
    placeOfSupply: '',
    billingType: 'Intra-State',
    gstRate: 18
  });

  const fetchMasterData = async () => {
    try {
      const [clientRes, bankRes, sessionRes, invoiceRes] = await Promise.all([
        api.get('/clients'),
        api.get('/company-banking-details'),
        api.get('/sessions').catch(() => ({ data: [] })),
        api.get('/invoices').catch(() => ({ data: [] }))
      ]);
      setClients(clientRes.data);
      setBankingList(bankRes.data);
      setSessions(sessionRes.data);
      setInvoices(invoiceRes.data);

      if (bankRes.data.length > 0 && !selectedBanking) {
        const activeBank = bankRes.data.find(b => b.status === 'Active') || bankRes.data[0];
        setSelectedBanking(activeBank.bankingDetailsId || activeBank._id);
      }
    } catch (err) {
      console.error('Error fetching data', err);
      toast.error('Failed to load master dropdown data');
    }
  };

  useEffect(() => {
    fetchMasterData();
  }, []);

  const currentClient = clients.find(c => c.clientId === selectedClient || c._id === selectedClient);
  const currentBank = bankingList.find(b => b.bankingDetailsId === selectedBanking || b._id === selectedBanking);

  // Auto-generate Invoice Number only when creating a new invoice AND both client and session are chosen
  useEffect(() => {
    if (!editId) {
      if (!selectedClient || !selectedSession) {
        setInvoiceData(prev => ({ ...prev, invoiceNumber: '' }));
        return;
      }

      const clientCode = currentClient?.clientCode || currentClient?.clientId || 'GENERAL';
      const sessionStr = selectedSession;

      const sessionInvoices = invoices.filter(inv => inv.session === sessionStr);
      let maxSeq = 0;

      sessionInvoices.forEach(inv => {
        const parts = inv.invoiceNumber ? inv.invoiceNumber.split('/') : [];
        const lastPart = parts[parts.length - 1];
        const seqNum = parseInt(lastPart, 10);
        if (!isNaN(seqNum) && seqNum > maxSeq) {
          maxSeq = seqNum;
        }
      });

      const nextSeqStr = String(maxSeq + 1).padStart(4, '0');
      const generatedNo = `SFT/${clientCode}/${sessionStr}/${nextSeqStr}`;
      setInvoiceData(prev => ({ ...prev, invoiceNumber: generatedNo }));
    }
  }, [selectedClient, selectedSession, invoices, editId, currentClient]);

  const resetForm = () => {
    setEditId(null);
    setSelectedClient('');
    setSelectedSession('');
    setBillingPeriods(['']);
    setItems([{ description: '', hsnSac: '998314', quantity: '', rate: '' }]);
    setInvoiceData({
      invoiceNumber: '',
      invoiceDate: getFormattedDate(),
      placeOfSupply: '',
      billingType: 'Intra-State',
      gstRate: 18
    });
  };

  // Calculations for multiple items
  const processedItems = items.map(item => {
    const qty = item.quantity === '' ? 0 : Number(item.quantity);
    const rt = item.rate === '' ? 0 : Number(item.rate);
    const taxableValue = qty * rt;
    const itemGst = (taxableValue * Number(invoiceData.gstRate || 0)) / 100;
    const cgst = invoiceData.billingType === 'Intra-State' ? itemGst / 2 : 0;
    const sgst = invoiceData.billingType === 'Intra-State' ? itemGst / 2 : 0;
    const igst = invoiceData.billingType === 'Inter-State' ? itemGst : 0;
    return { ...item, quantity: qty, rate: rt, taxableValue, cgst, sgst, igst };
  });

  const totalTaxableValue = processedItems.reduce((acc, curr) => acc + curr.taxableValue, 0);
  const totalGstAmount = processedItems.reduce((acc, curr) => acc + curr.cgst + curr.sgst + curr.igst, 0);
  const grandTotal = totalTaxableValue + totalGstAmount;

  // Handlers for dynamic billing periods
  const handleAddPeriod = () => setBillingPeriods([...billingPeriods, '']);
  const handlePeriodChange = (index, val) => {
    const updated = [...billingPeriods];
    updated[index] = val;
    setBillingPeriods(updated);
  };
  const handleRemovePeriod = (index) => {
    if (billingPeriods.length === 1) return;
    setBillingPeriods(billingPeriods.filter((_, i) => i !== index));
  };

  // Handlers for dynamic items
  const handleAddItem = () => setItems([...items, { description: '', hsnSac: '998314', quantity: '', rate: '' }]);
  const handleItemChange = (index, field, val) => {
    const updated = [...items];
    updated[index][field] = val;
    setItems(updated);
  };
  const handleRemoveItem = (index) => {
    if (items.length === 1) return;
    setItems(items.filter((_, i) => i !== index));
  };

  const handleSaveInvoice = async (e) => {
    e.preventDefault();
    if (!selectedClient) {
      toast.error('Please select a client from the master list');
      return;
    }
    if (!selectedSession) {
      toast.error('Please select a session');
      return;
    }
    if (!invoiceData.invoiceNumber) {
      toast.error('Invoice number is required');
      return;
    }

    try {
      const payload = {
        invoiceNumber: invoiceData.invoiceNumber,
        invoiceDate: invoiceData.invoiceDate,
        billingPeriods: billingPeriods.filter(p => p.trim() !== ''),
        placeOfSupply: invoiceData.placeOfSupply,
        billingType: invoiceData.billingType,
        clientId: currentClient?.clientId || selectedClient,
        clientCode: currentClient?.clientCode || '',
        session: selectedSession,
        bankingDetailsId: currentBank?.bankingDetailsId || selectedBanking,
        items: processedItems,
        gstRate: invoiceData.gstRate,
        totalTaxableValue,
        totalGstAmount,
        grandTotal
      };

      if (editId) {
        await api.put(`/invoices/${editId}`, payload);
        toast.success('Invoice updated successfully!');
      } else {
        await api.post('/invoices', payload);
        toast.success('Invoice generated & saved successfully!');
      }

      resetForm();
      fetchMasterData();
    } catch (err) {
      const errorMsg = err.response?.data?.error || err.message;
      toast.error(`Error saving invoice: ${errorMsg}`);
    }
  };

  const handleEdit = (inv) => {
    setEditId(inv._id);
    setSelectedClient(inv.clientId || '');
    setSelectedBanking(inv.bankingDetailsId || '');
    setSelectedSession(inv.session || '');
    setBillingPeriods(inv.billingPeriods && inv.billingPeriods.length > 0 ? inv.billingPeriods : [inv.billingPeriod || '']);
    setItems(inv.items && inv.items.length > 0 ? inv.items : [{ description: '', hsnSac: '998314', quantity: '', rate: '' }]);
    setInvoiceData({
      invoiceNumber: inv.invoiceNumber || '',
      invoiceDate: inv.invoiceDate || getFormattedDate(),
      placeOfSupply: inv.placeOfSupply || '',
      billingType: inv.billingType || 'Intra-State',
      gstRate: inv.gstRate || 18
    });
    setShowList(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this invoice?')) return;
    try {
      await api.delete(`/invoices/${id}`);
      toast.success('Invoice deleted successfully');
      fetchMasterData();
    } catch (err) {
      toast.error('Failed to delete invoice');
    }
  };

  const handlePrintPdf = (inv) => {
    setPrintingInvoice(inv);
    setTimeout(() => {
      window.print();
    }, 100);
  };

  const activePrintInv = printingInvoice || {
    invoiceNumber: invoiceData.invoiceNumber,
    invoiceDate: invoiceData.invoiceDate,
    billingPeriods: billingPeriods,
    placeOfSupply: invoiceData.placeOfSupply,
    billingType: invoiceData.billingType,
    gstRate: invoiceData.gstRate,
    items: processedItems,
    totalTaxableValue,
    totalGstAmount,
    grandTotal
  };

  const printClient = clients.find(c => c.clientId === (printingInvoice ? printingInvoice.clientId : selectedClient) || c._id === (printingInvoice ? printingInvoice.clientId : selectedClient));
  const printBank = bankingList.find(b => b.bankingDetailsId === (printingInvoice ? printingInvoice.bankingDetailsId : selectedBanking) || b._id === (printingInvoice ? printingInvoice.bankingDetailsId : selectedBanking));

  return (
    <div className="space-y-2">
      {/* Main dashboard panel hidden during print */}
      <div className="print:hidden space-y-2">
        <div className="flex justify-end px-1">
          <button 
            onClick={() => { 
              if (showList) {
                resetForm();
                setShowList(false);
              } else {
                setShowList(true);
              }
            }} 
            className={masterPrimaryButton}
          >
            {showList ? 'Create New Invoice' : `View Saved Invoices (${invoices.length})`}
          </button>
        </div>

        {!showList ? (
          <div className={masterPanel}>
            <div className={masterFormContainer}>
              <h2 className={masterSectionTitle}>
                {editId ? 'Edit Tax Invoice' : 'Tax Invoice Generator Form'}
              </h2>
              
              <form onSubmit={handleSaveInvoice} className="space-y-2">
                <div className={masterFormGrid3}>
                  <div className={masterFormGridRow}>
                    <label className={masterInputLabel}>Select Client</label>
                    <select 
                      className={masterSelect} 
                      value={selectedClient} 
                      onChange={(e) => setSelectedClient(e.target.value)}
                      required
                    >
                      <option value="">-- Choose Client --</option>
                      {clients.map(client => (
                        <option key={client._id} value={client.clientId || client._id}>
                          {client.clientName}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className={masterFormGridRow}>
                    <label className={masterInputLabel}>Company Bank Account</label>
                    <select 
                      className={masterSelect} 
                      value={selectedBanking} 
                      onChange={(e) => setSelectedBanking(e.target.value)}
                      required
                    >
                      <option value="">-- Choose Bank Account --</option>
                      {bankingList.map(bank => (
                        <option key={bank._id} value={bank.bankingDetailsId || bank._id}>
                          {bank.bankName} - {bank.accountNumber} 
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className={masterFormGridRow}>
                    <label className={masterInputLabel}>Session</label>
                    <select 
                      className={masterSelect} 
                      value={selectedSession} 
                      onChange={(e) => setSelectedSession(e.target.value)}
                      required
                    >
                      <option value="">-- Choose Session --</option>
                      {sessions.map(s => (
                        <option key={s._id} value={s.session}>
                          {s.session}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className={masterFormGrid3}>
                  <div className={masterFormGridRow}>
                    <label className={masterInputLabel}>Invoice Number (Editable)</label>
                    <input 
                      type="text" 
                      value={invoiceData.invoiceNumber} 
                      onChange={e => setInvoiceData({...invoiceData, invoiceNumber: e.target.value})} 
                      className={masterInput} 
                      placeholder="Select client & session first"
                      required
                    />
                  </div>

                  <div className={masterFormGridRow}>
                    <label className={masterInputLabel}>Invoice Date (DD-MM-YYYY)</label>
                    <input 
                      type="text" 
                      placeholder="DD-MM-YYYY"
                      value={invoiceData.invoiceDate} 
                      onChange={e => setInvoiceData({...invoiceData, invoiceDate: e.target.value})} 
                      className={masterInput} 
                      required
                    />
                  </div>

                  <div className={masterFormGridRow}>
                    <label className={masterInputLabel}>Place of Supply</label>
                    <input 
                      type="text" 
                      placeholder="e.g. Kalyani, West Bengal"
                      value={invoiceData.placeOfSupply} 
                      onChange={e => setInvoiceData({...invoiceData, placeOfSupply: e.target.value})} 
                      className={masterInput} 
                    />
                  </div>
                </div>

                {/* Multiple Billing Periods Section (4 fields in a row) */}
                <div className="border border-[#358156]/20 p-2 rounded-lg space-y-1 bg-slate-50">
                  <div className="flex justify-between items-center">
                    <label className={masterInputLabel}>Billing Periods (Multiple)</label>
                    <button type="button" onClick={handleAddPeriod} className="px-2 py-0.5 bg-[#358156] text-white rounded text-[10px] font-bold uppercase">
                      + Add Period
                    </button>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-2">
                    {billingPeriods.map((period, idx) => (
                      <div key={idx} className="flex gap-1 items-center">
                        <input 
                          type="month" 
                          value={period} 
                          onChange={e => handlePeriodChange(idx, e.target.value)} 
                          className={masterInput} 
                          required
                        />
                        {billingPeriods.length > 1 && (
                          <button type="button" onClick={() => handleRemovePeriod(idx)} className="px-1.5 py-1 bg-red-500 text-white rounded text-[10px] font-bold">
                            X
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                <div className={masterFormGrid3}>
                  <div className={masterFormGridRow}>
                    <label className={masterInputLabel}>Billing Type</label>
                    <select 
                      value={invoiceData.billingType} 
                      onChange={e => setInvoiceData({...invoiceData, billingType: e.target.value})} 
                      className={masterSelect}
                    >
                      <option value="Intra-State">Intra-State (CGST + SGST)</option>
                      <option value="Inter-State">Inter-State (IGST)</option>
                    </select>
                  </div>

                  <div className={masterFormGridRow}>
                    <label className={masterInputLabel}>GST Rate (%)</label>
                    <input 
                      type="number" 
                      value={invoiceData.gstRate} 
                      onChange={e => setInvoiceData({...invoiceData, gstRate: Number(e.target.value)})} 
                      className={masterInput} 
                      required
                    />
                  </div>

                  <div className={masterFormGridRow}>
                    <label className={masterInputLabel}>Calculated Grand Total</label>
                    <div className="px-2 py-1 bg-slate-100 border border-slate-300 rounded text-xs font-bold text-[#358156]">
                      ₹ {grandTotal.toFixed(2)} (Taxable: {totalTaxableValue.toFixed(2)} + GST: {totalGstAmount.toFixed(2)})
                    </div>
                  </div>
                </div>

                {/* Multiple Line Items Section */}
                <div className="border border-[#358156]/20 p-2 rounded-lg space-y-2 bg-slate-50">
                  <div className="flex justify-between items-center">
                    <label className={masterInputLabel}>Service Descriptions & Items (Multiple)</label>
                    <button type="button" onClick={handleAddItem} className="px-2 py-0.5 bg-[#358156] text-white rounded text-[10px] font-bold uppercase">
                      + Add Item
                    </button>
                  </div>
                  {items.map((item, idx) => (
                    <div key={idx} className="grid grid-cols-1 sm:grid-cols-12 gap-1 items-center border-b pb-2 border-slate-200">
                      <div className="sm:col-span-5">
                        <input 
                          type="text" 
                          placeholder="Description of Service"
                          value={item.description} 
                          onChange={e => handleItemChange(idx, 'description', e.target.value)} 
                          className={masterInput} 
                          required
                        />
                      </div>
                      <div className="sm:col-span-2">
                        <input 
                          type="text" 
                          placeholder="HSN/SAC"
                          value={item.hsnSac} 
                          onChange={e => handleItemChange(idx, 'hsnSac', e.target.value)} 
                          className={masterInput} 
                          required
                        />
                      </div>
                      <div className="sm:col-span-1">
                        <input 
                          type="number" 
                          placeholder="Qty"
                          value={item.quantity} 
                          onChange={e => handleItemChange(idx, 'quantity', e.target.value)} 
                          className={masterInput} 
                          required
                        />
                      </div>
                      <div className="sm:col-span-3">
                        <input 
                          type="number" 
                          placeholder="Rate"
                          value={item.rate} 
                          onChange={e => handleItemChange(idx, 'rate', e.target.value)} 
                          className={masterInput} 
                          required
                        />
                      </div>
                      <div className="sm:col-span-1 flex justify-center">
                        {items.length > 1 && (
                          <button type="button" onClick={() => handleRemoveItem(idx)} className="px-2 py-1 bg-red-500 text-white rounded text-[10px] font-bold">
                            X
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                <div className={masterFormActions}>
                  {editId && (
                    <button type="button" onClick={resetForm} className="px-2 py-1 bg-slate-500 text-white rounded text-[10px] font-bold uppercase">
                      Cancel
                    </button>
                  )}
                  <button type="submit" className={masterAccentButton}>
                    {editId ? 'Update & Download PDF' : 'Save & Download PDF'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        ) : (
          /* Saved Invoices List View */
          <div className={masterPanel}>
            <div className={masterTableWrap}>
              <table className={masterTable}>
                <thead>
                  <tr className={masterStickyHeadRow}>
                    <th className={masterTableHeaderCell}>Invoice No</th>
                    <th className={masterTableHeaderCell}>Date</th>
                    <th className={masterTableHeaderCell}>Session</th>
                    <th className={masterTableHeaderCell}>Client ID</th>
                    <th className={masterTableHeaderCell}>Grand Total</th>
                    <th className={masterTableHeaderCellCenter}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {invoices.map(inv => (
                    <tr key={inv._id} className={masterTableRow}>
                      <td className={masterTableData}>{inv.invoiceNumber}</td>
                      <td className={masterTableData}>{inv.invoiceDate}</td>
                      <td className={masterTableData}>{inv.session || 'N/A'}</td>
                      <td className={masterTableData}>{inv.clientId}</td>
                      <td className={masterTableData}>{inv.grandTotal?.toFixed(2)}</td>
                      <td className={masterTableActionCell}>
                        <div className="flex justify-center gap-1">
                          <button onClick={() => handleEdit(inv)} className={masterEditButton}>
                            Edit
                          </button>
                          <button onClick={() => handlePrintPdf(inv)} className="px-2 py-0.5 bg-[#358156] text-white rounded text-[10px] font-bold uppercase">
                            PDF
                          </button>
                          <button onClick={() => handleDelete(inv._id)} className={masterDeleteButton}>
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {invoices.length === 0 && (
                    <tr>
                      <td colSpan="6" className="text-center p-4 text-xs text-slate-500">No saved invoices found.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Hidden Printable Bill Format Container */}
      <div className="hidden print:block font-sans text-[10px] text-black bg-white p-2 leading-tight">
        {/* Header Section */}
        <div className="relative flex justify-center items-start border-b border-black pb-2 mb-1">
          <div className="absolute left-0 top-0">
            <img src={logo} alt="Logo" className="w-20 h-20 rounded-full object-contain" />
          </div>
          <div className="text-center">
            <h1 className="font-bold text-sm tracking-wide mb-1">SIXFOX TECHNOLOGY</h1>
            <p className="text-[9px] font-semibold">Software Development & Maintenance Services</p>
            <p className="text-[9px] font-semibold">A11/424, Kalyani, Nadia, West Bengal – 741235</p>
            <div className="flex justify-center gap-4 text-[9px] mt-0.5">
              <span><span className="font-bold">GSTIN:</span> <span className="font-semibold">19AFQPC6063Z28</span></span>
              <span><span className="font-bold">PAN:</span> <span className="font-semibold">AFQPC6063G</span></span>
            </div>
            <h2 className="font-bold text-xs uppercase mt-1 tracking-wider">TAX INVOICE</h2>
          </div>
        </div>

        {/* Metadata Grid */}
        <div className="grid grid-cols-2 gap-x-6 mb-2 text-[10px]">
          <div className="space-y-0.5">
            <div className="grid grid-cols-[135px_10px_1fr]">
              <span className="font-bold">Invoice Number</span><span>:</span><span className="font-semibold">{activePrintInv.invoiceNumber}</span>
            </div>
            <div className="grid grid-cols-[135px_10px_1fr]">
                  <span className="font-bold">Billing Periods</span><span>:</span>
                  <span className="font-semibold">
                    {activePrintInv.billingPeriods?.map(p => formatBillingPeriod(p)).join(', ') || '-'}
                  </span>
                </div>
          </div>
          <div className="space-y-0.5">
            <div className="grid grid-cols-[105px_10px_1fr]">
              <span className="font-bold">Invoice Date</span><span>:</span><span className="font-semibold">{activePrintInv.invoiceDate}</span>
            </div>
            <div className="grid grid-cols-[105px_10px_1fr]">
              <span className="font-bold">Place of Supply</span><span>:</span><span className="font-semibold">{activePrintInv.placeOfSupply || '-'}</span>
            </div>
            <div className="grid grid-cols-[105px_10px_1fr]">
              <span className="font-bold">Billing Type</span><span>:</span><span className="font-semibold">{activePrintInv.billingType}</span>
            </div>
          </div>
        </div>

        {/* Bill To Section */}
        <div className="mb-2 text-[10px] space-y-0.5">
          <p className="font-bold">Bill To:</p>
          <div className="grid grid-cols-[110px_10px_1fr]">
            <span className="font-bold">Client Name</span><span>:</span><span className="font-semibold">{printClient?.clientName || activePrintInv.clientId || 'N/A'}</span>
          </div>
          <div className="grid grid-cols-[110px_10px_1fr]">
            <span className="font-bold">Client Address</span><span>:</span><span className="font-semibold">{printClient?.address || 'N/A'}</span>
          </div>
          <div className="grid grid-cols-[110px_10px_1fr]">
            <span className="font-bold">Client GSTIN</span><span>:</span><span className="font-semibold">{printClient?.gstin || 'N/A'}</span>
          </div>
        </div>

        {/* Line Items Table */}
        <table className="w-full border-collapse border border-black text-[10px] mb-2 text-center">
          <thead>
            <tr className="bg-gray-100">
              <th className="border border-black p-1 w-10 font-bold">Sl No</th>
              <th className="border border-black p-1 text-left px-2 font-bold">Description of Service</th>
              <th className="border border-black p-1 w-16 font-bold">HSN/SAC</th>
              <th className="border border-black p-1 w-12 font-bold">Quantity</th>
              <th className="border border-black p-1 w-16 font-bold">Rate</th>
              <th className="border border-black p-1 w-20 font-bold">Taxable Value</th>
              <th className="border border-black p-1 w-12 font-bold">CGST</th>
              <th className="border border-black p-1 w-12 font-bold">SGST</th>
              <th className="border border-black p-1 w-12 font-bold">IGST</th>
            </tr>
          </thead>
          <tbody>
            {activePrintInv.items?.map((item, idx) => (
              <tr key={idx}>
                <td className="border border-black p-1 align-top font-semibold">{idx + 1}</td>
                <td className="border border-black p-1 text-left px-2 align-top font-semibold">{item.description || '-'}</td>
                <td className="border border-black p-1 align-top font-semibold">{item.hsnSac || '-'}</td>
                <td className="border border-black p-1 align-top font-semibold">{item.quantity || 0}</td>
                <td className="border border-black p-1 align-top font-semibold">{item.rate || 0}</td>
                <td className="border border-black p-1 align-top font-semibold">{Number(item.taxableValue).toFixed(2)}</td>
                <td className="border border-black p-1 align-top font-semibold">
                  {activePrintInv.billingType === 'Intra-State' ? Number(item.cgst).toFixed(2) : '0.00'}
                </td>
                <td className="border border-black p-1 align-top font-semibold">
                  {activePrintInv.billingType === 'Intra-State' ? Number(item.sgst).toFixed(2) : '0.00'}
                </td>
                <td className="border border-black p-1 align-top font-semibold">
                  {activePrintInv.billingType === 'Inter-State' ? Number(item.igst).toFixed(2) : '0.00'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Totals Summary */}
        <div className="flex justify-end mb-2 text-[10px]">
          <div className="w-64 space-y-0.5">
            <div className="flex justify-between"><span className="font-bold">GST Rate (%)</span> <span className="font-semibold">{activePrintInv.gstRate}</span></div>
            <div className="flex justify-between"><span className="font-bold">Billing Type</span> <span className="font-semibold">{activePrintInv.billingType}</span></div>
            <div className="flex justify-between border-t border-black pt-0.5"><span className="font-bold">Total Taxable Value</span> <span className="font-semibold">{Number(activePrintInv.totalTaxableValue).toFixed(2)}</span></div>
            <div className="flex justify-between"><span className="font-bold">Total GST Amount</span> <span className="font-semibold">{Number(activePrintInv.totalGstAmount).toFixed(2)}</span></div>
            <div className="flex justify-between border-t border-black pt-0.5"><span className="font-bold">Grand Total</span> <span className="font-bold">{Number(activePrintInv.grandTotal).toFixed(2)}</span></div>
          </div>
        </div>

        {/* Bank & Payment Details */}
        <div className="mb-2 text-[9px] space-y-0.5">
          <p className="font-bold text-[12px]">Bank & Payment Details</p>
          <div className="grid grid-cols-[120px_10px_1fr]">
            <span className="font-bold">Bank Name</span><span>:</span><span className="font-semibold">{printBank?.bankName || '-'}</span>
          </div>
          <div className="grid grid-cols-[120px_10px_1fr]">
            <span className="font-bold">Account Number</span><span>:</span><span className="font-semibold">{printBank?.accountNumber || '-'}</span>
          </div>
          <div className="grid grid-cols-[120px_10px_1fr]">
            <span className="font-bold">IFSC Code</span><span>:</span><span className="font-semibold">{printBank?.ifscCode || '-'}</span>
          </div>
          <div className="grid grid-cols-[120px_10px_1fr]">
            <span className="font-bold">Branch Name</span><span>:</span><span className="font-semibold">{printBank?.branchName || '-'}</span>
          </div>
          <div className="grid grid-cols-[120px_10px_1fr]">
            <span className="font-bold">Account Holder's Name</span><span>:</span><span className="font-semibold">{printBank?.holderName || '-'}</span>
          </div>
        </div>

        {/* Declaration Footer */}
        <div className="text-[9px] italic border-t border-black pt-1">
          <p>**We declare that this invoice shows the actual price of the services described and that all particulars are true and correct.</p>
        </div>
      </div>
    </div>
  );
};

export default InvoiceGenerator;